/**
 * Okno „Zmeniť hrany aj v dielcoch?" (KOLO 222).
 *
 * User: *„výber hrán v materiálovom slote by sa mal správať ako globálne
 * nastavenie — teraz keď už máš vybrané hrany a zmeníš ich v slote,
 * neaktualizuje ich v dielcoch… keď zmeníš, vypíše ti, že sa menia…
 * keby niekto vyberie ručne na dielci nejakú hranu, tak tá globálna zmena
 * sa toho netýka."*
 *
 * Každá zmena materiálu zákazky, ktorá mení hrany (bunka tabuľky, ↺,
 * kurácia, iná doska), ide cez `useOknoZmenyHran().spusti`: najprv náhľad
 * zo servera — keď sa žiadnemu dielcu hrana nezmení, uloží sa rovno; inak
 * okno so zoznamom a voľbou **Zmeniť aj v dielcoch / Len pre nové dielce /
 * Zrušiť**. Ktorá strana ide podľa materiálu, rozhoduje server
 * (`services/hrany_slotu`), okno len ukazuje jeho odpoveď.
 */
import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useZmenaHranSlotu } from "@/api/hooks/useSlotEdges";
import type {
  MaterialSummary,
  SlotZmenaHranRequest,
  SlotZmenaHranResponse,
  ZmenaHranDielca,
} from "@/api/schemas";
import { rozmerHrany } from "@/lib/format";

/** Hrana krátko — dekor a rozmer, celý názov v tooltipe. */
function hranaKratko(m: MaterialSummary | null | undefined): string {
  if (!m) return "—";
  const rozmer = rozmerHrany(m.sirka_mm, m.hrubka_mm);
  return [m.dekor_kod, rozmer].filter(Boolean).join(" ");
}

interface RiadokOkna {
  kluc: string;
  zostava: string;
  dielec: string;
  strany: string[];
  zo: MaterialSummary | null | undefined;
  na: MaterialSummary | null | undefined;
}

/** Strany toho istého dielca s rovnakou zmenou do jedného riadku. */
export function riadkyOkna(polozky: readonly ZmenaHranDielca[]): RiadokOkna[] {
  const out = new Map<string, RiadokOkna>();
  for (const p of polozky) {
    const kluc = `${p.dielec_id}|${p.zo?.id ?? ""}|${p.na?.id ?? ""}`;
    const riadok = out.get(kluc);
    if (riadok) {
      riadok.strany.push(p.strana.toUpperCase());
      continue;
    }
    out.set(kluc, {
      kluc,
      zostava: p.zostava_kod ?? "",
      dielec: [p.kod, p.nazov].filter(Boolean).join(" "),
      strany: [p.strana.toUpperCase()],
      zo: p.zo,
      na: p.na,
    });
  }
  return [...out.values()];
}

/** Čo sa mení — náhľad a zápis si `nahlad` / `do_dielcov` doplní okno. */
export type ZmenaHranTelo = Omit<SlotZmenaHranRequest, "nahlad" | "do_dielcov">;

function pocetDielcov(n: number): string {
  if (n === 1) return "1 dielci";
  return `${n} dielcoch`;
}

interface Otvorene {
  telo: ZmenaHranTelo;
  popis: string;
  nahlad: SlotZmenaHranResponse;
  hotovo: (ulozene: boolean) => void;
}

export interface OknoZmenyHran {
  /**
   * Náhľad → (okno) → zápis. Vráti `true`, keď sa zmena uložila (aj len
   * pre nové dielce), `false` pri Zrušiť alebo chybe (tú ohlási toastom).
   */
  spusti: (telo: ZmenaHranTelo, popis: string) => Promise<boolean>;
  okno: JSX.Element;
}

export function useOknoZmenyHran(slotId: string, slotKod: string): OknoZmenyHran {
  const mutacia = useZmenaHranSlotu(slotId);
  const [otvorene, setOtvorene] = useState<Otvorene | null>(null);
  const [uklada, setUklada] = useState(false);
  // Okno sa dá zavrieť aj krížikom — výsledok musí prísť práve raz
  const vybavene = useRef(false);

  async function uloz(
    telo: ZmenaHranTelo,
    popis: string,
    doDielcov: boolean,
  ): Promise<SlotZmenaHranResponse | null> {
    try {
      return await mutacia.mutateAsync({
        telo: { ...telo, nahlad: false, do_dielcov: doDielcov },
        popis,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Hrany sa nepodarilo uložiť",
        description: extractErrorMessage(err),
      });
      return null;
    }
  }

  function upozornenieNechanych(v: SlotZmenaHranResponse): void {
    if (v.nechane.length === 0) return;
    const dielce = new Set(v.nechane.map((n) => n.dielec_id)).size;
    toast({
      title: `V ${pocetDielcov(dielce)} ostala stará hrana`,
      description:
        "Materiál pre ich hrúbku hranu nemá alebo je úzka na dosku — vyber ju v tabuľke hrán.",
    });
  }

  async function spusti(telo: ZmenaHranTelo, popis: string): Promise<boolean> {
    let nahlad: SlotZmenaHranResponse;
    try {
      nahlad = await mutacia.mutateAsync({
        telo: { ...telo, nahlad: true, do_dielcov: true },
        popis,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
      return false;
    }
    if (nahlad.zmeny.length === 0) {
      // Žiadnemu dielcu sa hrana nezmení — niet sa čo pýtať
      const v = await uloz(telo, popis, true);
      if (v) upozornenieNechanych(v);
      return v !== null;
    }
    vybavene.current = false;
    return new Promise<boolean>((resolve) => {
      setOtvorene({ telo, popis, nahlad, hotovo: resolve });
    });
  }

  function zavri(ulozene: boolean): void {
    if (!otvorene || vybavene.current) return;
    vybavene.current = true;
    otvorene.hotovo(ulozene);
    setOtvorene(null);
  }

  async function potvrd(doDielcov: boolean): Promise<void> {
    if (!otvorene) return;
    setUklada(true);
    const v = await uloz(otvorene.telo, otvorene.popis, doDielcov);
    setUklada(false);
    if (v && doDielcov) upozornenieNechanych(v);
    zavri(v !== null);
  }

  const nahlad = otvorene?.nahlad;
  const riadky = nahlad ? riadkyOkna(nahlad.zmeny) : [];
  const nechane = nahlad ? riadkyOkna(nahlad.nechane) : [];
  const stran = nahlad?.zmeny.length ?? 0;

  const okno = (
    <Dialog open={otvorene !== null} onOpenChange={(o) => !o && !uklada && zavri(false)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Zmeniť hrany aj v dielcoch?</DialogTitle>
          <DialogDescription>
            Zmena v materiáli {slotKod} mení hranu v {pocetDielcov(nahlad?.dielcov ?? 0)} (
            {stran} {stran === 1 ? "strana" : stran < 5 ? "strany" : "strán"}). Ručne vybrané
            hrany na dielcoch sa nemenia.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-background text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left">Zostava</th>
                <th className="px-2 py-1 text-left">Dielec</th>
                <th className="px-2 py-1 text-left">Strany</th>
                <th className="px-2 py-1 text-left">Hrana</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {riadky.map((r) => (
                <tr key={r.kluc}>
                  <td className="px-2 py-1 font-mono">{r.zostava}</td>
                  <td className="px-2 py-1">{r.dielec}</td>
                  <td className="px-2 py-1 font-mono">{r.strany.join(", ")}</td>
                  <td className="px-2 py-1 whitespace-nowrap">
                    <span title={r.zo?.nazov} className="text-muted-foreground line-through">
                      {hranaKratko(r.zo)}
                    </span>
                    {" → "}
                    <span title={r.na?.nazov} className="font-medium">
                      {hranaKratko(r.na)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {nechane.length > 0 ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
            <p className="font-medium">
              Ostane stará hrana — materiál pre ich hrúbku hranu nemá alebo je úzka:
            </p>
            <ul className="mt-0.5">
              {nechane.map((r) => (
                <li key={r.kluc}>
                  {r.zostava} {r.dielec} · {r.strany.join(", ")} · {hranaKratko(r.zo)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="ghost" disabled={uklada} onClick={() => zavri(false)}>
            Zrušiť
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={uklada}
            onClick={() => void potvrd(false)}
            title="Existujúce dielce si nechajú doterajšie hrany"
          >
            Len pre nové dielce
          </Button>
          <Button type="button" disabled={uklada} onClick={() => void potvrd(true)}>
            Zmeniť aj v dielcoch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { spusti, okno };
}
